import { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import React, { useState } from 'react'

interface HPOCategorizationProps {
  categorizedTerms: CategorizedHPOOption[]
  onCategoryChange: (termId: string, newCategory: 'Key' | 'Additional' | 'Excluded') => void
  title?: string
  readOnly?: boolean
}

const HPOCategorization: React.FC<HPOCategorizationProps> = ({
  categorizedTerms,
  onCategoryChange,
  title = 'Categorize New Added HPO Terms',
  readOnly = false,
}) => {
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set())

  const getCategoryColors = (category: 'Key' | 'Additional' | 'Excluded') => {
    const colors = {
      Key: 'bg-green-100 text-green-800 border-green-200',
      Additional: 'bg-blue-100 text-blue-800 border-blue-200',
      Excluded: 'bg-red-100 text-red-800 border-red-200',
    }
    return colors[category]
  }

  const getFirstWrappedLine = (text: string, maxLength: number = 80): string => {
    // If text is shorter than maxLength, return the whole text
    if (text.length <= maxLength) {
      return text
    }

    // Find the last space before maxLength to avoid cutting words
    const truncated = text.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')

    if (lastSpaceIndex > maxLength * 0.7) {
      // If we found a space in a reasonable position
      return text.substring(0, lastSpaceIndex) + '...'
    } else {
      return truncated + '...'
    }
  }

  const isMultiLine = (text: string): boolean => {
    return text.length > 80 // Show expand button for longer text
  }

  const toggleExpanded = (termId: string) => {
    setExpandedTerms((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(termId)) {
        newSet.delete(termId)
      } else {
        newSet.add(termId)
      }
      return newSet
    })
  }

  if (categorizedTerms.length === 0) {
    return null
  }

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <h3 className="text-md mb-2 font-medium text-gray-900">{title}</h3>
      <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 max-h-64 space-y-3 overflow-y-auto pr-2">
        {categorizedTerms.map((term) => (
          <div
            key={term.id}
            className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-blue-600">{term.id}</span>
                <span className="truncate text-sm font-medium text-gray-900">{term.label}</span>
              </div>
              {term.node.meta?.definition?.val && term.node.meta?.definition?.val !== term.label && (
                <div className="mt-1">
                  <p className="text-xs text-gray-600">
                    {expandedTerms.has(term.id)
                      ? term.node.meta?.definition?.val
                      : getFirstWrappedLine(term.node.meta?.definition?.val)}
                  </p>
                  {isMultiLine(term.node.meta?.definition?.val) && (
                    <button
                      onClick={() => toggleExpanded(term.id)}
                      className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      {expandedTerms.has(term.id) ? (
                        <>
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          Show less
                        </>
                      ) : (
                        <>
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          Show more
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="ml-4 flex items-center gap-2">
              <span className="text-xs text-gray-500">Category:</span>
              {readOnly ? (
                <span className={`rounded border px-2 py-1 text-xs ${getCategoryColors(term.category)}`}>
                  {term.category}
                </span>
              ) : (
                <div className="flex gap-1">
                  {(['Key', 'Additional', 'Excluded'] as const).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => onCategoryChange(term.id, cat)}
                      className={`rounded border px-2 py-1 text-xs transition-colors ${
                        term.category === cat
                          ? getCategoryColors(cat)
                          : 'border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default HPOCategorization
